// src/pages/sessions/components/LiveSession/whiteboard/WhiteboardBottomBar.jsx
import React, { useState, useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// ===== TOOL ICONS =====

// Select/Arrow cursor icon
const SelectIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 3l14 11-6 1-3 6-5-18z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" />
  </svg>
);

// Marker icon (like a felt-tip marker with round tip)
const MarkerIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M15.5 4.5l4 4L9 19H5v-4L15.5 4.5z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.5 7.5l4 4" strokeLinecap="round" />
  </svg>
);

// Highlighter icon (wide chisel tip marker at angle)
const HighlighterIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Wide chisel-tip highlighter body */}
    <path d="M4 14l3-3 6 6-3 3H4v-6z" fill="currentColor" opacity="0.3" stroke="none" />
    <path d="M7 11l10-10 3 3-10 10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 14l3-3 6 6-3 3H4v-6z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 4l3 3" strokeLinecap="round" />
  </svg>
);

// Thick Marker icon (chisel-tip felt marker)
const ThickMarkerIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 20l2.5-2.5M14.5 5.5l4 4L10 18H6v-4L14.5 5.5z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14.5 5.5l1.5-1.5a1.414 1.414 0 012 0l2 2a1.414 1.414 0 010 2L18.5 9.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.5 8.5l4 4" strokeLinecap="round" />
  </svg>
);

// Eraser icon
const EraserIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M20 20H9L4 15l10-10 6 6-5 5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 12l6 6" strokeLinecap="round" />
  </svg>
);

// Shapes icon
const ShapesIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <circle cx="17" cy="17" r="4" />
  </svg>
);

// Line icon
const LineIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="19" x2="19" y2="5" strokeLinecap="round" />
  </svg>
);

// Arrow icon
const ArrowIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="19" x2="19" y2="5" strokeLinecap="round" />
    <polyline points="10,5 19,5 19,14" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Rectangle icon
const RectangleIcon = ({ className, filled }) => (
  <svg className={className} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <rect x="4" y="6" width="16" height="12" rx="1" />
  </svg>
);

// Circle icon
const CircleIcon = ({ className, filled }) => (
  <svg className={className} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="8" />
  </svg>
);

// Text icon
const TextIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 4h12M12 4v16M9 20h6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Image icon
const ImageIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
    <path d="M21 15l-5-5L5 21" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Detach icon (expand to new window)
const DetachIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 3h6v6M14 10l6.1-6.1M9 21H3v-6M10 14l-6.1 6.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Attach icon (collapse back)
const AttachIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 14h6v6M3 21l6.1-6.1M20 10h-6V4M21 3l-6.1 6.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Color palette - matching Google Classroom
const COLORS = [
  '#000000', // Black
  '#FF0000', // Red
  '#FF9900', // Orange
  '#FFFF00', // Yellow
  '#00FF00', // Green
  '#00FFFF', // Cyan
  '#0066FF', // Blue
  '#9933FF', // Purple
  '#FF66CC', // Pink
];

// Size options
const SIZES = [
  { value: 2, dotSize: 'w-2 h-2' },
  { value: 4, dotSize: 'w-3 h-3' },
  { value: 8, dotSize: 'w-4 h-4' },
];

const WhiteboardBottomBar = memo(({
  activeTool,
  toolSettings,
  isTeacher,
  canDraw,
  studentDrawingEnabled,
  onToolChange,
  onToolSettingsChange,
  onClear,
  onClose,
  onToggleStudentDrawing,
  isDetached,
  onDetach,
  onAttach,
}) => {
  const { t } = useTranslation();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showShapesMenu, setShowShapesMenu] = useState(false);
  const [compact, setCompact] = useState(false);
  const clearConfirmRef = useRef(null);
  const shapesMenuRef = useRef(null);
  const barRef = useRef(null);

  // Get current tool settings
  const currentSettings = toolSettings?.[activeTool] || { color: '#000000', size: 4 };
  const activeColor = currentSettings.color;
  const brushSize = currentSettings.size;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clearConfirmRef.current && !clearConfirmRef.current.contains(e.target)) {
        setShowClearConfirm(false);
      }
      if (shapesMenuRef.current && !shapesMenuRef.current.contains(e.target)) {
        setShowShapesMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect narrow layout via ResizeObserver
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      setCompact(entry.contentRect.width < 900);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const showDrawingTools = canDraw;
  
  // Tools that support color/size
  const colorSupportedTools = ['marker', 'highlighter', 'brush', 'line', 'arrow', 'rectangle', 'circle', 'filledRectangle', 'filledCircle', 'text'];
  const isColorSupported = colorSupportedTools.includes(activeTool);
  
  // Shape tools
  const shapeTools = ['line', 'arrow', 'rectangle', 'circle', 'filledRectangle', 'filledCircle'];
  const isShapeTool = shapeTools.includes(activeTool);

  const handleColorChange = (color) => {
    if (onToolSettingsChange && isColorSupported) {
      onToolSettingsChange(activeTool, { color });
    }
  };

  const handleSizeChange = (size) => {
    if (onToolSettingsChange && isColorSupported) {
      onToolSettingsChange(activeTool, { size });
    }
  };

  const handleShapeSelect = (shape) => {
    onToolChange(shape);
    setShowShapesMenu(false);
  };

  const handleClearConfirm = () => {
    setShowClearConfirm(false);
    if (onClear) onClear();
  };

  // Tool button component
  const ToolButton = ({ tool, icon: Icon, title, activeClass = 'bg-cyan-600 text-white', onClick }) => (
    <button
      onClick={onClick || (() => onToolChange(tool))}
      className={`p-2 rounded-lg transition-all ${
        activeTool === tool
          ? activeClass
          : 'text-gray-300 hover:bg-gray-600'
      }`}
      title={title}
    >
      <Icon className="w-5 h-5" />
    </button>
  );

  // ===== Color & Size strip (shared between layouts) =====
  const ColorSizeStrip = () => (
    isColorSupported ? (
      <div className="flex items-center">
        <div className="flex items-center space-x-1">
          {COLORS.map(color => (
            <button
              key={color}
              onClick={() => handleColorChange(color)}
              className={`w-5 h-5 rounded-full transition-all flex items-center justify-center ${
                activeColor === color 
                  ? 'ring-2 ring-offset-1 ring-offset-gray-700 ring-cyan-400 scale-110' 
                  : 'hover:scale-110'
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
        <div className="w-1 h-1 rounded-full bg-gray-500 mx-2" />
        <div className="flex items-center space-x-1.5">
          {SIZES.map((size, idx) => (
            <button
              key={size.value}
              onClick={() => handleSizeChange(size.value)}
              className={`rounded-full transition-all flex items-center justify-center p-1 ${
                brushSize === size.value
                  ? 'ring-2 ring-offset-1 ring-offset-gray-700 ring-cyan-400'
                  : 'hover:ring-2 hover:ring-offset-1 hover:ring-offset-gray-700 hover:ring-gray-500'
              }`}
              title={`Size ${idx + 1}`}
            >
              <div className={`${size.dotSize} rounded-full bg-gray-200`} />
            </button>
          ))}
        </div>
      </div>
    ) : null
  );

  // ===== Tool buttons strip =====
  const ToolStrip = () => (
    <div className="flex items-center">
      <ToolButton tool="select" icon={SelectIcon} title={t('whiteboard.tools.select', 'Select')} />
      <div className="w-px h-5 bg-gray-500 mx-1" />
      <ToolButton tool="marker" icon={MarkerIcon} title={t('whiteboard.tools.marker', 'Pen')} />
      <ToolButton tool="brush" icon={ThickMarkerIcon} title={t('whiteboard.tools.thickMarker', 'Marker')} />
      <button
        onClick={() => onToolChange('highlighter')}
        className={`p-2 rounded-lg transition-all ${
          activeTool === 'highlighter'
            ? 'bg-yellow-500 text-white'
            : 'text-gray-300 hover:bg-gray-600'
        }`}
        title={t('whiteboard.tools.highlighter', 'Highlighter')}
      >
        <HighlighterIcon className="w-5 h-5" />
      </button>
      <ToolButton tool="eraser" icon={EraserIcon} title={t('whiteboard.tools.eraser', 'Eraser')} />
      
      {/* Shapes Dropdown */}
      <div className="relative" ref={shapesMenuRef}>
        <button
          onClick={() => setShowShapesMenu(!showShapesMenu)}
          className={`p-2 rounded-lg transition-all ${
            isShapeTool ? 'bg-cyan-600 text-white' : 'text-gray-300 hover:bg-gray-600'
          }`}
          title={t('whiteboard.tools.shapes', 'Shapes')}
        >
          <ShapesIcon className="w-5 h-5" />
        </button>
        
        {showShapesMenu && (
          <div className="absolute bottom-full left-0 mb-2 p-2 bg-gray-800 rounded-xl shadow-lg border border-gray-600 grid grid-cols-3 gap-1 min-w-[140px] z-50">
            <button onClick={() => handleShapeSelect('line')} className={`p-2.5 rounded-lg hover:bg-gray-600 ${activeTool === 'line' ? 'bg-cyan-600' : ''}`} title={t('whiteboard.shapes.line', 'Line')}>
              <LineIcon className="w-5 h-5 text-gray-200" />
            </button>
            <button onClick={() => handleShapeSelect('arrow')} className={`p-2.5 rounded-lg hover:bg-gray-600 ${activeTool === 'arrow' ? 'bg-cyan-600' : ''}`} title={t('whiteboard.shapes.arrow', 'Arrow')}>
              <ArrowIcon className="w-5 h-5 text-gray-200" />
            </button>
            <button onClick={() => handleShapeSelect('rectangle')} className={`p-2.5 rounded-lg hover:bg-gray-600 ${activeTool === 'rectangle' ? 'bg-cyan-600' : ''}`} title={t('whiteboard.shapes.rectangle', 'Rectangle')}>
              <RectangleIcon className="w-5 h-5 text-gray-200" />
            </button>
            <button onClick={() => handleShapeSelect('circle')} className={`p-2.5 rounded-lg hover:bg-gray-600 ${activeTool === 'circle' ? 'bg-cyan-600' : ''}`} title={t('whiteboard.shapes.circle', 'Circle')}>
              <CircleIcon className="w-5 h-5 text-gray-200" />
            </button>
            <button onClick={() => handleShapeSelect('filledRectangle')} className={`p-2.5 rounded-lg hover:bg-gray-600 ${activeTool === 'filledRectangle' ? 'bg-cyan-600' : ''}`} title={t('whiteboard.shapes.filledRectangle', 'Filled Rectangle')}>
              <RectangleIcon className="w-5 h-5 text-gray-200" filled />
            </button>
            <button onClick={() => handleShapeSelect('filledCircle')} className={`p-2.5 rounded-lg hover:bg-gray-600 ${activeTool === 'filledCircle' ? 'bg-cyan-600' : ''}`} title={t('whiteboard.shapes.filledCircle', 'Filled Circle')}>
              <CircleIcon className="w-5 h-5 text-gray-200" filled />
            </button>
          </div>
        )}
      </div>
      
      <ToolButton tool="text" icon={TextIcon} title={t('whiteboard.tools.text', 'Text')} />
      <ToolButton tool="image" icon={ImageIcon} title={t('whiteboard.tools.image', 'Insert Image')} />
    </div>
  );

  // ===== Action buttons (clear, student toggle, detach, close) =====
  const ActionButtons = () => (
    <div className="flex items-center space-x-1">
      {showDrawingTools && isTeacher && (
        <div className="relative" ref={clearConfirmRef}>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="p-2 rounded-lg text-gray-300 hover:bg-red-600 hover:text-white transition-colors"
            title={t('whiteboard.tools.clear', 'Clear All')}
          >
            <TrashIcon className="w-5 h-5" />
          </button>
          
          {showClearConfirm && (
            <div className="absolute bottom-full right-0 mb-2 p-3 bg-gray-800 rounded-xl shadow-lg border border-gray-600 w-64 z-50">
              <div className="flex items-start space-x-2 mb-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">
                    {t('whiteboard.clearConfirm.title', 'Clear whiteboard?')}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {t('whiteboard.clearConfirm.message', 'This will remove all drawings.')}
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button onClick={() => setShowClearConfirm(false)} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors">
                  {t('whiteboard.clearConfirm.cancel', 'Cancel')}
                </button>
                <button onClick={handleClearConfirm} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  {t('whiteboard.clearConfirm.confirm', 'Clear All')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isTeacher && (
        <button
          onClick={() => onToggleStudentDrawing(!studentDrawingEnabled)}
          className={`relative flex items-center space-x-1.5 px-2 py-1.5 rounded-lg transition-colors ${
            studentDrawingEnabled
              ? 'bg-green-600/20 text-green-300'
              : 'bg-gray-700/50 text-gray-400'
          }`}
          title={t('whiteboard.permissions.studentDrawing', 'Student Drawing')}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
          </svg>
          {!compact && (
            <span className="text-xs font-medium">
              {t('whiteboard.permissions.studentsLabel', 'Students')}
            </span>
          )}
          <div className={`relative w-8 h-[18px] rounded-full transition-colors ${
            studentDrawingEnabled ? 'bg-green-500' : 'bg-gray-600'
          }`}>
            <div className={`absolute top-[3px] w-3 h-3 rounded-full bg-white shadow transition-transform ${
              studentDrawingEnabled ? 'translate-x-[14px]' : 'translate-x-[3px]'
            }`} />
          </div>
        </button>
      )}
      
      {isTeacher && (
        <>
          {!isDetached && onDetach && (
            <button onClick={onDetach} className="p-2 rounded-lg text-gray-300 hover:bg-indigo-600 hover:text-white transition-colors" title={t('whiteboard.detach', 'Open in new window')}>
              <DetachIcon className="w-5 h-5" />
            </button>
          )}
          {isDetached && onAttach && (
            <button onClick={onAttach} className="p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors" title={t('whiteboard.attach', 'Return to session')}>
              <AttachIcon className="w-5 h-5" />
            </button>
          )}
          <button onClick={onClose} className="p-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors" title={t('whiteboard.close', 'Close Whiteboard')}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div ref={barRef} className="whiteboard-bottom-bar bg-gray-800 border-t border-gray-700 px-3 py-2">
      {showDrawingTools ? (
        compact ? (
          /* ===== COMPACT (narrow): two rows ===== */
          <div className="flex flex-col gap-1.5">
            {/* Top row: tools + actions */}
            <div className="flex items-center justify-between">
              <div className="bg-gray-700 rounded-xl px-1.5 py-0.5 border border-gray-600">
                <ToolStrip />
              </div>
              <ActionButtons />
            </div>
            {/* Bottom row: colors + sizes, centered */}
            {isColorSupported && (
              <div className="flex justify-center bg-gray-700 rounded-xl px-2 py-1 border border-gray-600">
                <ColorSizeStrip />
              </div>
            )}
          </div>
        ) : (
          /* ===== WIDE: single row ===== */
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex items-center bg-gray-700 rounded-xl px-2 py-1 shadow-sm border border-gray-600">
                <ToolStrip />
                {isColorSupported && (
                  <>
                    <div className="w-px h-5 bg-gray-500 mx-2" />
                    <ColorSizeStrip />
                  </>
                )}
              </div>
            </div>
            <ActionButtons />
          </div>
        )
      ) : (
        /* ===== VIEW ONLY ===== */
        <div className="flex items-center justify-between">
          <div className="text-gray-400 text-sm px-3 py-2 bg-gray-700 rounded-xl">
            {t('whiteboard.viewOnly', 'View only')}
          </div>
          <ActionButtons />
        </div>
      )}
    </div>
  );
});

WhiteboardBottomBar.displayName = 'WhiteboardBottomBar';

export default WhiteboardBottomBar;
